import { getBioData } from '@/utils/bioUtils';
import { Box, Chip, Link, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { Link as RouterLink } from 'react-router-dom';
import useLastFm from '../../../hooks/useLastFm';
import useWindowSize from '../../../hooks/useWindowSize';
import { BREAKPOINT, getArtistPath, linkSongsInBio, trackClick } from '../../_helpers';

export function BioSection(props) {
  const { artistData } = props;
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;

  // Fetch Last.fm data
  const { data: lastFmData, loading, error } = useLastFm(artistData?.name, [artistData?.name]);

  if (!artistData) {
    return null;
  }

  const {
    bio: cleanBio,
    songMappings,
    bioSongMentions,
  } = getBioData(artistData?.name, lastFmData?.bio?.summary, !!error, artistData, lastFmData);

  // Check if we have any actual data to display
  const hasTags = lastFmData?.tags && lastFmData.tags.length > 0;
  const hasSimilarArtists = lastFmData?.similarArtists && lastFmData.similarArtists.length > 0;
  const hasAnyData = cleanBio || hasTags || hasSimilarArtists;
  const bioLinkMentions = [
    ...new Set([
      ...bioSongMentions,
      ...((artistData as any)?.topTracks?.map((track: any) => track.name) || []),
    ]),
  ];

  // Hide section if no actual bio data (not loading)
  if (!loading && !hasAnyData) {
    return null;
  }

  return (
    <Box
      className="BioSection"
      sx={{
        position: 'relative',
        width: `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: { xs: '4rem 2rem', md: '3.5rem' },
        m: { xs: '1rem 2.5rem 0', md: '12.25rem 2.5rem 0' },
        backgroundColor: 'var(--color5)',
        borderRadius: '1.875rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: { xs: '2.5rem', md: '2.5rem' },
        zIndex: 1,
      }}
    >
      {/* Header */}
      <Box
        className="BioHeader"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: { xs: '2rem', md: '1rem' },
        }}
      >
        <Typography
          className="Bio"
          sx={{
            color: 'var(--logoColor2)',
            fontSize: { xs: '3rem', md: '3.34rem' },
            fontFamily: 'Hanson',
            fontWeight: '700',
          }}
        >
          BIO
        </Typography>

        {lastFmData?.listeners && (
          <Typography
            sx={{
              color: 'var(--logoColor)',
              fontSize: { xs: '1.5rem', md: '1rem' },
              fontFamily: 'Poppins',
              fontWeight: '400',
            }}
          >
            {parseInt(String(lastFmData.listeners)).toLocaleString()} listeners
          </Typography>
        )}
      </Box>

      {/* Bio Content - Show short blurb (~100 words) */}
      {cleanBio ? (
        <Box
          sx={{
            width: '100%',
            color: 'var(--logoColor)',
            fontSize: { xs: '2.5rem', md: '1.4rem' },
            fontFamily: 'Poppins',
            fontWeight: '400',
            lineHeight: { xs: 1.65, md: 1.9 },
          }}
        >
          {(() => {
            const wordLimit = reallyIsMobile ? 34 : 100;
            const words = cleanBio.split(/\s+/);
            const truncatedBio =
              words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : cleanBio;
            return linkSongsInBio(truncatedBio, artistData?.name, bioLinkMentions, songMappings);
          })()}
        </Box>
      ) : loading ? (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Box
            sx={{
              width: '100%',
              height: '1.25rem',
              backgroundColor: 'var(--logoColor)',
              opacity: 0.2,
              borderRadius: '0.25rem',
            }}
          />
          <Box
            sx={{
              width: '90%',
              height: '1.25rem',
              backgroundColor: 'var(--logoColor)',
              opacity: 0.2,
              borderRadius: '0.25rem',
            }}
          />
          <Box
            sx={{
              width: '75%',
              height: '1.25rem',
              backgroundColor: 'var(--logoColor)',
              opacity: 0.2,
              borderRadius: '0.25rem',
            }}
          />
        </Box>
      ) : (
        <Typography
          sx={{
            width: '100%',
            color: 'var(--logoColor)',
            fontSize: { xs: '2.1rem', md: '1.4rem' },
            fontFamily: 'Poppins',
            fontWeight: '400',
            lineHeight: 1.9,
            fontStyle: 'italic',
            opacity: 0.7,
          }}
        >
          {`Learn more about ${artistData.name} by exploring their music and following their latest releases.`}
        </Typography>
      )}

      {/* Tags */}
      {hasTags && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: '1rem', md: '0.75rem' },
          }}
        >
          {lastFmData.tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              sx={{
                backgroundColor: 'var(--logoColor2)',
                color: 'var(--color5)',
                fontFamily: 'Poppins',
                fontSize: { xs: '1.15rem', md: '1rem' },
                fontWeight: '600',
                borderRadius: '0.5rem',
                padding: { xs: '0.5rem', md: '0' },
                '&:hover': {
                  backgroundColor: 'var(--logoColor)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Similar Artists */}
      {hasSimilarArtists && (
        <Box sx={{ width: '100%', mt: { xs: '1rem', md: '1rem' } }}>
          <Typography
            sx={{
              color: 'var(--logoColor2)',
              fontSize: { xs: '2rem', md: '1.67rem' },
              fontFamily: 'Monument Extended',
              fontWeight: '700',
              mb: { xs: '1.5rem', md: '1.5rem' },
            }}
          >
            Similar Artists
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: '1.5rem', md: '1.5rem' },
            }}
          >
            {lastFmData.similarArtists.map((artist, index) => (
              <Link
                key={index}
                href={artist.url}
                onClick={() => trackClick('bio-similar-artist', { label: artist.name, section: 'bio', href: artist.url })}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {artist.image && (
                  <Box
                    component="img"
                    src={artist.image}
                    alt={artist.name}
                    sx={{
                      width: { xs: '6rem', md: '4rem' },
                      height: { xs: '6rem', md: '4rem' },
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                )}
                <Typography
                  sx={{
                    color: 'var(--logoColor2)',
                    fontSize: { xs: '1.15rem', md: '1rem' },
                    fontFamily: 'Poppins',
                    fontWeight: '600',
                    textAlign: 'center',
                    maxWidth: { xs: '6rem', md: '4rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {artist.name}
                </Typography>
              </Link>
            ))}
          </Box>
        </Box>
      )}

      {/* Link to Full Bio Page */}
      <Link
        to={getArtistPath(artistData?.name, '/bio')}
        component={RouterLink}
        onClick={() => trackClick('bio-read-full', { label: 'Read full bio', section: 'bio', artist: artistData?.name, href: getArtistPath(artistData?.name, '/bio') })}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: { xs: '1.35rem 2.5rem', md: '1.1rem 2rem' },
          backgroundColor: 'var(--logoColor2)',
          color: 'var(--color5)',
          borderRadius: '0.5rem',
          fontSize: { xs: '2rem', md: '1.15rem' },
          fontFamily: 'Hanson',
          fontWeight: '700',
          textDecoration: 'none',
          textAlign: 'center',
          transition: 'all 0.3s ease',
          letterSpacing: '0.075rem',
          mt: { xs: '1rem', md: '0.5rem' },
          '&:hover': {
            backgroundColor: 'var(--logoColor)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        VIEW FULL BIO
      </Link>
    </Box>
  );
}
